import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/apiClient";
import { Podcast, ChevronDown, Loader2, FileText, Download } from "lucide-react";
import { AiContentBadge } from "@/components/LegalConsent";

// Podcast formativi (Fase 1) — lato partner. Player audio per le tracce accessibili.
export function ProductPodcasts({ code, name }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScript, setShowScript] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/products/${code}/podcast`); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !data) load(); };

  const download = async (kind, label) => {
    setDownloading(kind);
    try {
      const { data: blob } = await api.get(`/products/${code}/podcast/${kind}/audio`, { responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const fileName = `${name} - ${label}`.replace(/[^\w\s.-]/g, "").trim().replace(/\s+/g, "_");
      a.href = url;
      a.download = `${fileName}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(null); }
  };

  return (
    <div className="mt-3 rounded-lg border border-terracotta/20 bg-[#FBF6F3]" data-testid={`podcast-${code}`}>
      <button onClick={toggle} data-testid={`podcast-toggle-${code}`}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-terracotta-hover">
          <Podcast className="h-4 w-4" /> Podcast formativi
        </span>
        <ChevronDown className={`h-4 w-4 text-terracotta-hover transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-terracotta/10 px-4 py-4" data-testid={`podcast-body-${code}`}>
          {loading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
          ) : !data || !data.items?.length ? (
            <p className="py-1 text-sm text-muted-foreground">
              {data?.status === "in_generazione" ? "Podcast in generazione, riprova tra poco." : "Nessun podcast disponibile per questo prodotto."}
            </p>
          ) : (
            <div className="space-y-4">
              <AiContentBadge variant="audio" />
              {data.items.map((it) => (
                <div key={it.kind} data-testid={`podcast-track-${code}-${it.kind}`}>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-forest">{it.label}</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => download(it.kind, it.label)} disabled={downloading === it.kind}
                        className="flex items-center gap-1 text-xs text-terracotta-hover hover:text-terracotta disabled:opacity-50" data-testid={`podcast-download-${code}-${it.kind}`}>
                        {downloading === it.kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Scarica MP3
                      </button>
                      <button onClick={() => setShowScript(showScript === it.kind ? null : it.kind)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-forest" data-testid={`podcast-script-toggle-${code}-${it.kind}`}>
                        <FileText className="h-3.5 w-3.5" /> Testo
                      </button>
                    </div>
                  </div>
                  <audio controls preload="none" className="w-full" data-testid={`podcast-audio-${code}-${it.kind}`}
                    src={`${API}/products/${code}/podcast/${it.kind}/audio`} />
                  {showScript === it.kind && (
                    <p className="mt-2 whitespace-pre-line rounded bg-white/70 p-3 text-xs leading-relaxed text-muted-foreground">{it.script}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
